import React from 'react';
import { MapPin, Calendar, Users, Luggage, Car } from 'lucide-react';

const OrderSummary = ({ tripDetails, vehicleDetails, selectedExtras = [] }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Date not set';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  // Handle loading state - don't render if no vehicle details
  if (!vehicleDetails) {
    return (
      <div className="bg-white rounded-xl overflow-hidden shadow-sm" data-testid="order-summary">
        <div className="bg-gray-50 px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-black">Order Summary</h3>
        </div>
        <div className="p-5">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-sm" data-testid="order-summary">
      {/* Header */}
      <div className="bg-gray-50 px-5 py-4 border-b border-gray-100">
        <h3 className="font-semibold text-black">Order Summary</h3>
      </div>

      <div className="p-5 space-y-4">
        {/* Route */}
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-[#FF8C00]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <div className="w-2 h-2 rounded-full bg-[#FF8C00]" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">From</p>
              <p className="text-sm font-medium text-black">{tripDetails.pickup}</p>
            </div>
          </div>

          <div className="ml-3 w-0.5 h-3 bg-gray-200 border-l-2 border-dashed border-gray-300" />

          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-[#FF8C00] flex items-center justify-center flex-shrink-0 mt-0.5">
              <MapPin className="h-3 w-3 text-white" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">To</p>
              <p className="text-sm font-medium text-black">{tripDetails.dropoff}</p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100" />

        {/* Date & Time */}
        <div className="flex items-center gap-3">
          <Calendar className="h-4 w-4 text-gray-400" />
          <div>
            <p className="text-sm text-black">
              {formatDate(tripDetails.date)} at {tripDetails.time}
            </p>
          </div>
        </div>

        {/* Vehicle */}
        <div className="flex items-center gap-3">
          <Car className="h-4 w-4 text-gray-400" />
          <p className="text-sm text-black">{vehicleDetails.name}</p>
        </div>

        {/* Passengers & Luggage */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-black">{tripDetails.passengers}</span>
          </div>
          <div className="flex items-center gap-2">
            <Luggage className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-black">{tripDetails.luggage}</span>
          </div>
        </div>

        {/* Extras */}
        {selectedExtras.length > 0 && (
          <>
            <div className="border-t border-gray-100" />
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Extras</p>
              <ul className="space-y-1">
                {selectedExtras.map((extra, index) => (
                  <li key={index} className="text-sm text-black flex justify-between">
                    <span>{extra.name} {extra.quantity > 1 && `(×${extra.quantity})`}</span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderSummary;
